import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  BellAlertIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { useConfirm } from "../contexts/ConfirmContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const ITEMS_PER_PAGE = 10;
const PLACEHOLDER_IMG =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0EzQTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";

const Home = () => {
  const { confirm } = useConfirm();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "",
    source: "",
    published: false,
    image: null,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashList, setTrashList] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const resolveImageUrl = (url) => {
    if (url == null || (typeof url === "string" && !url.trim())) return null;
    const str = typeof url === "string" ? url.trim() : String(url);
    if (/^(?:[a-z]+:)?\/\//i.test(str)) return str;
    const path = str.startsWith("/storage/") || str.startsWith("storage/")
      ? "/files/" + str.replace(/^\/?storage\/?/, "")
      : str.startsWith("/files/") || str.startsWith("files/")
      ? (str.startsWith("/") ? str : "/" + str)
      : "/files/" + (str.startsWith("/") ? str.slice(1) : str);
    let base = (API_URL || "").replace(/\/$/, "");
    if (base && !base.endsWith("/api")) base = base + "/api";
    return base ? `${base}${path}` : path;
  };

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/news`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setNews(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching news:", error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrash = async () => {
    try {
      setTrashLoading(true);
      const res = await axios.get(`${API_URL}/admin/news/trash`, {
        headers: getAuthHeaders(),
      });
      setTrashList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setTrashList([]);
      toast.error("Failed to load deleted notifications");
    } finally {
      setTrashLoading(false);
    }
  };

  const openTrashModal = () => {
    setShowTrashModal(true);
    fetchTrash();
  };

  useEffect(() => {
    fetchNews();
    fetchTrash();
    const timeoutId = setTimeout(() => {
      if (loading) setLoading(false);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, image: file });
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const isValidUrl = (value) => {
    if (!value || typeof value !== "string") return true;
    const trimmed = value.trim();
    if (trimmed === "") return true;
    return /^https?:\/\/.+/.test(trimmed);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Form: first field is name="title" (Description/Category), second is name="category" (Title *). API expects title=short title, category=optional desc.
    const titleValueTrimmed = (form.category || "").trim();
    if (!titleValueTrimmed) {
      toast.error("Title is required.");
      return;
    }
    const sourceTrimmed = (form.source || "").trim();
    if (sourceTrimmed && !isValidUrl(form.source)) {
      toast.error("Source Link must be a valid URL (e.g. https://...)");
      return;
    }
    const formData = new FormData();
    formData.append("title", titleValueTrimmed);
    formData.append("published", form.published);
    if (form.title) formData.append("category", form.title);
    formData.append("source", sourceTrimmed);
    if (form.image instanceof File) formData.append("image", form.image);

    try {
      if (selectedNews) {
        await axios.put(
          `${API_URL}/admin/news/${selectedNews.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(`${API_URL}/admin/news`, formData, {
          headers: getAuthHeaders(),
        });
      }
      resetForm();
      setShowFormModal(false);
      fetchNews();
      toast.success(selectedNews ? "Notification updated" : "Notification created");
    } catch (error) {
      console.error("Error saving news:", error);
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      toast.error(serverMessage || "Failed to save notification.");
    }
  };

  const resetForm = () => {
    setForm({
      category: "",
      title: "",
      source: "",
      published: false,
      image: null,
    });
    setPreviewUrl(null);
    setSelectedNews(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.category || "",
      category: item.title || "",
      source: item.source || "",
      published: item.published || false,
      image: null,
    });
    setPreviewUrl(item.image_url ? resolveImageUrl(item.image_url) : null);
    setSelectedNews(item);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete notification",
      message: "Are you sure you want to delete this notification? It will be moved to the Delete Bin.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/admin/news/${id}`, {
        headers: getAuthHeaders(),
      });
      fetchNews();
      fetchTrash();
      setShowDetailModal(false);
      setSelectedNews(null);
      toast.success("Notification moved to Delete Bin");
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.patch(`${API_URL}/admin/news/${id}/restore`, {}, {
        headers: getAuthHeaders(),
      });
      fetchTrash();
      fetchNews();
      toast.success("Notification restored");
    } catch (err) {
      toast.error("Failed to restore notification");
    }
  };

  const handlePermanentDelete = async (id) => {
    const ok = await confirm({
      title: "Permanent delete",
      message: "Permanently delete this notification? This cannot be undone.",
      confirmLabel: "Delete permanently",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/admin/news/${id}/permanent`, {
        headers: getAuthHeaders(),
      });
      fetchTrash();
      fetchNews();
      toast.success("Notification permanently deleted");
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  const handlePublish = async (item, publishState) => {
    try {
      await axios.put(
        `${API_URL}/admin/news/${item.id}`,
        { ...item, published: publishState },
        { headers: getAuthHeaders() }
      );
      fetchNews();
      if (selectedNews && selectedNews.id === item.id) {
        setSelectedNews({ ...item, published: publishState });
      }
      toast.success(publishState ? "Published" : "Unpublished");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const filteredNews = news.filter(
    (item) =>
      (item.title &&
        item.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.category &&
        item.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.source &&
        item.source.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredNews.length / ITEMS_PER_PAGE)
  );
  const paginatedNews = filteredNews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50 overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 p-4 md:p-6">
        {/* Stats row - same pattern as Courses (Total, Published, Unpublished, Delete Bin) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-indigo-100 text-indigo-600">
              <BellAlertIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Notifications
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {filteredNews.length}
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-green-100 text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Published
              </p>
              <p className="mt-1 text-2xl font-bold text-green-600">
                {filteredNews.filter((n) => n.published).length}
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-amber-100 text-amber-600">
              <PauseCircleIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unpublished
              </p>
              <p className="mt-1 text-2xl font-bold text-amber-600">
                {filteredNews.filter((n) => !n.published).length}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openTrashModal}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm text-left hover:border-red-300 hover:bg-red-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-start gap-3"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 text-red-600">
              <TrashIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Delete Bin
              </p>
              <p className="mt-1 text-2xl font-bold text-red-600">
                {trashList.length}
              </p>
            </div>
          </button>
        </div>

        {/* Header: title, search, Add button - same as Courses */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:flex-initial sm:w-56">
              <input
                type="text"
                placeholder="Search notifications"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <button
              onClick={openAddModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Notification
            </button>
          </div>
        </div>

        {/* Table - same structure as Courses */}
        <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded-lg bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-12"
                >
                  #
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-20"
                >
                  Image
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Title
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Source Link
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedNews.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                  <td className="px-3 py-3 text-sm font-medium text-gray-500 whitespace-nowrap">
                    {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <img
                      src={
                        item.image_url
                          ? resolveImageUrl(item.image_url)
                          : PLACEHOLDER_IMG
                      }
                      alt=""
                      className="h-10 w-10 rounded-lg object-cover border border-gray-200 bg-gray-100"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = PLACEHOLDER_IMG;
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[120px] truncate">
                    {item.category || "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[220px] truncate"
                    title={item.title}
                  >
                    {item.title || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {item.source && (item.source.startsWith("http://") || item.source.startsWith("https://")) ? (
                      <a
                        href={item.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        Link
                      </a>
                    ) : (
                      <span className="text-gray-400">No Link</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublish(item, !item.published);
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        item.published ? "bg-green-500" : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={item.published}
                      title={
                        item.published
                          ? "Published (click to Unpublish)"
                          : "Unpublished (click to Publish)"
                      }
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                          item.published
                            ? "translate-x-5"
                            : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedNews(item);
                          setShowDetailModal(true);
                        }}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredNews.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-gray-500 text-sm"
                  >
                    No notifications found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - same as Courses */}
        {filteredNews.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4 px-1 border-t border-gray-200 bg-white rounded-b-lg">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">
                {(currentPage - 1) * ITEMS_PER_PAGE + 1}
              </span>
              –
              <span className="font-medium">
                {Math.min(
                  currentPage * ITEMS_PER_PAGE,
                  filteredNews.length
                )}
              </span>{" "}
              of <span className="font-medium">{filteredNews.length}</span>{" "}
              notifications
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.max(1, p - 1))
                }
                disabled={currentPage <= 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page{" "}
                <span className="font-medium">{currentPage}</span> of{" "}
                <span className="font-medium">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage >= totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal – enhanced UI, larger popup & inputs */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border-0 flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center px-8 py-6 border-b border-gray-100">
              <h3 className="text-2xl font-semibold text-gray-900">
                {selectedNews ? "Edit Notification" : "Create Notification"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  resetForm();
                }}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                title="Close"
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto min-h-0 p-8 space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900 uppercase tracking-wider">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-200 px-5 py-4 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
                    placeholder="e.g. New course announcement"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900 uppercase tracking-wider">
                    Description / Category <span className="text-gray-500 font-normal normal-case">(optional)</span>
                  </label>
                  <textarea
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    rows={5}
                    className="block w-full rounded-xl border border-gray-200 px-5 py-4 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow resize-y min-h-[120px]"
                    placeholder="e.g. Events or short description"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900 uppercase tracking-wider">
                    Image (Recommended: 1200×400px)
                  </label>
                  <input
                    type="file"
                    name="image"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="block w-full text-base text-gray-900 file:mr-4 file:py-3 file:px-5 file:rounded-xl file:border-0 file:text-base file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 file:transition-colors"
                  />
                  {previewUrl && (
                    <div className="mt-2 relative h-52 w-full bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900 uppercase tracking-wider">
                    Source Link <span className="text-gray-500 font-normal normal-case">(optional, URL only)</span>
                  </label>
                  <input
                    type="text"
                    name="source"
                    value={form.source}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-200 px-5 py-4 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
                    placeholder="https://example.com/..."
                  />
                </div>
              </div>

              {/* Footer - fixed at bottom, does not scroll */}
              <div className="flex-shrink-0 px-8 py-5 bg-gray-50 border-t border-gray-100 flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFormModal(false);
                    resetForm();
                  }}
                  className="flex-1 py-3 text-base font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm"
                >
                  {selectedNews ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Detail Modal – view-only, larger popup & text */}
      {showDetailModal && selectedNews && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedNews(null);
          }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border-0 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image hero */}
            <div className="relative bg-gray-100">
              {selectedNews.image_url ? (
                <img
                  src={resolveImageUrl(selectedNews.image_url)}
                  alt={selectedNews.title || "Notification"}
                  className="w-full h-56 sm:h-72 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = "none";
                    e.target.nextElementSibling?.classList.remove("hidden");
                  }}
                />
              ) : null}
              <div
                className={`w-full h-56 sm:h-72 flex items-center justify-center text-gray-400 ${
                  selectedNews.image_url ? "hidden" : ""
                }`}
              >
                <BellAlertIcon className="w-20 h-20 text-gray-300" />
              </div>
              {/* Status pill on image */}
              <div className="absolute top-4 right-4">
                <span
                  className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium shadow-sm ${
                    selectedNews.published
                      ? "bg-emerald-500/90 text-white"
                      : "bg-amber-500/90 text-white"
                  }`}
                >
                  {selectedNews.published ? "Published" : "Unpublished"}
                </span>
              </div>
              {/* Close button */}
              <button
                type="button"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedNews(null);
                }}
                className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                title="Close"
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 leading-snug">
                  {selectedNews.title || "—"}
                </h2>
              </div>

              <div className="grid gap-5">
                {selectedNews.category && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">
                      Category
                    </span>
                    <span className="text-base text-gray-900">
                      {selectedNews.category}
                    </span>
                  </div>
                )}
                {selectedNews.source && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">
                      Source Link
                    </span>
                    <span className="text-base text-gray-900">
                      {selectedNews.source}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">
                    Created
                  </span>
                  <span className="text-base text-gray-900">
                    {selectedNews.created_at
                      ? new Date(selectedNews.created_at).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </span>
                </div>
                {selectedNews.updated_at && selectedNews.updated_at !== selectedNews.created_at && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-gray-900 uppercase tracking-wider">
                      Last updated
                    </span>
                    <span className="text-base text-gray-900">
                      {new Date(selectedNews.updated_at).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer: single Close */}
            <div className="px-8 py-5 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedNews(null);
                }}
                className="w-full py-3 text-base font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Bin modal */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-xl border border-gray-200 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <TrashIcon className="h-6 w-6 text-red-500" />
                Delete Bin
              </h3>
              <button
                type="button"
                onClick={() => setShowTrashModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                title="Close"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {trashLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                </div>
              ) : trashList.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No deleted notifications.</p>
              ) : (
                <ul className="space-y-2">
                  {trashList.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{item.title || "—"}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {item.category || "—"} • Deleted
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleRestore(item.id)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Restore"
                        >
                          <ArrowUturnLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePermanentDelete(item.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Permanent delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
